import React, { useCallback, useEffect, useState } from "react";
import { Alert, Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { format, parseISO } from "date-fns";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Button, Input } from "../../../shared/ui/FormElements";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { useAuth } from "../../../../context/AuthContext";
import { Storage } from "../../../services/storage/storage";
import { StorageKeys } from "../../../services/storage/keys";
import { DandvatService } from "../../../services/DandvatService";
import { DandvatsModel, SaveDandvatsModel } from "../../../models/DandvatsModel";
import { Flash } from "../../../shared/utils/Flash";

type Nav = NativeStackNavigationProp<AuthStackParamList>;

export function DandvatsScreen() {
    const navigation = useNavigation<Nav>();
    const { user } = useAuth();

    // ✅ MUST be actual GUID userId
    const [userId, setUserId] = useState<string>("");

    const [date, setDate] = useState(DandvatService.todayStr()); // yyyy-MM-dd
    const [count, setCount] = useState("");
    const [marks, setMarks] = useState(0);
    const [summary, setSummary] = useState({ totalCount: 0, totalMarks: 0 });
    const [dandvatId, setdandvatId] = useState<string | null>(null);
    const [isEditable, setIsEditable] = useState(true);


    // ✅ Single API call: loads day entry + month totals
    const loadAll = async (dateStr: string) => {
        try {
            // Your API expects date only in payload (as per your current service)
            const payload: DandvatsModel = { date: dateStr };

            const res = await DandvatService.getAgainstDate(payload);
            const data = res?.data;

            if (!data) {
                setdandvatId(null);
                setCount("");
                setMarks(0);
                setSummary({ totalCount: 0, totalMarks: 0 });
                return;
            }

            setdandvatId(data.dandvatId);
            setCount(data.numberOfDandvats ? String(data.numberOfDandvats) : "");
            setMarks(data.marksOfDandvats ?? 0);
            setIsEditable(data.isEditable);


            setSummary({
                totalCount: data.totalDandvats ?? 0,
                totalMarks: data.totalMarks ?? 0,
            });
        } catch (e: any) {
            Alert.alert("Error", e?.message ?? "Failed to load Dandvats");
        }
    };

    // ✅ resolve GUID userId once (from AuthContext or Storage)
    useEffect(() => {
        (async () => {
            let uid = (user as any)?.userId; // <-- make sure your auth user has userId

            if (!uid) {
                const raw = await Storage.get(StorageKeys.currentUser);
                const u = raw ? JSON.parse(raw) : null;
                uid = u?.userId;
            }

            if (uid) setUserId(String(uid));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ exactly ONE call on first load and every date change
    useFocusEffect(
        useCallback(() => {
            if (!userId || !date) return;

            loadAll(date);

            // optional cleanup
            return () => { };
        }, [userId, date])
    );

    const openDatePicker = () => {
        DateTimePickerAndroid.open({
            value: new Date(date),
            mode: "date",
            maximumDate: new Date(),
            onChange: (_event, selected) => {
                if (!selected) return;
                setDate(format(selected, "yyyy-MM-dd")); // triggers API call
            },
        });
    };

    const onCountChange = (v: string) => {
        const digits = v.replace(/\D/g, "");
        setCount(digits);

        const n = digits ? parseInt(digits, 10) : 0;

        // UI marks can be decimal (display)
        setMarks(Number((n / 3).toFixed(2)));
    };

    const save = async () => {
        if (!userId) {
            Alert.alert("Error", "User not found");
            return;
        }

        if (!count) {
            Alert.alert("Required", "Please enter number of Dandvats");
            return;
        }

        try {
            const countNum = parseInt(count, 10);
            if (Number.isNaN(countNum) || countNum < 0) {
                Alert.alert("Invalid", "Please enter valid number of Dandvats");
                return;
            }

            // ✅ API-2 payload: matches your C# DandvatsDto
            // Marks is INT in your DTO, so we must send int.
            var payload = {
                dandvatId: dandvatId,
                userId: userId,
                date: date,
                count: countNum,
                marks: parseFloat((countNum / 3).toFixed(2)) // ✅ 0.33 based
            };

            // ✅ Call save API (2nd API)
            
            const res = await DandvatService.saveDandvat(payload);
            if (res.status != 200) {
                Flash.error(res?.message);
                await loadAll(date);
                return;
            }
            // ✅ refresh again via 1st API
            await loadAll(date);

            Flash.success("Success!", "Dandvats Saved Successfully!");
            navigation.navigate("DandvatsHistory");

        } catch (e: any) {
            Flash.error(e?.message ?? "Failed to save");
        }
    };

    return (
        <DivineBackground overlayOpacity={0.95}>
            <Header title="Dandvats Seva" showBack showProfile />

            <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
                {/* Summary */}
                <View style={styles.summaryCard}>
                    <View style={styles.summaryHead}>
                        <View style={styles.summaryIcon}>
                            <Text style={{ fontWeight: "900", color: "#C2410C" }}>∞</Text>
                        </View>
                        <Text style={styles.summaryTitle}>This Month&apos;s Offering</Text>
                    </View>

                    <View style={styles.summaryGrid}>
                        <View style={styles.summaryBox}>
                            <Text style={styles.summaryLabel}>TOTAL DANDVATS</Text>
                            <Text style={styles.summaryValueOrange}>{summary.totalCount}</Text>
                        </View>
                        <View style={styles.summaryBox}>
                            <Text style={styles.summaryLabel}>TOTAL MARKS</Text>
                            <Text style={styles.summaryValueAmber}>{summary.totalMarks}</Text>
                        </View>
                    </View>
                </View>

                {/* Form card */}
                <View style={styles.card}>
                    <View style={styles.topRow}>
                        <Pressable onPress={openDatePicker} style={styles.datePill}>
                            <Text style={styles.calIcon}>📅</Text>
                            <Text style={styles.dateText}>{format(parseISO(date), "dd-MM-yyyy")}</Text>
                        </Pressable>

                        <Pressable onPress={() => navigation.navigate("DandvatsHistory")} style={styles.historyBtn}>
                            <Text style={styles.historyIcon}>🕘</Text>
                            <Text style={styles.historyText}>History</Text>
                        </Pressable>
                    </View>

                    <Input
                        label="Number of Dandvats"
                        placeholder="e.g. 12"
                        keyboardType="number-pad"
                        value={count}
                        onChangeText={onCountChange}
                    />
                    <Text style={styles.hint}>3 Dandvats = 1 Mark</Text>

                    <View style={styles.marksRow}>
                        <Text style={styles.marksLabel}>Calculated Marks:</Text>
                        <Text style={styles.marksValue}>{marks}</Text>
                    </View>

                    {isEditable && (
                        <View style={styles.btnRow}>
                            {/* ✅ Cancel button visible (custom Pressable) */}
                            <Pressable onPress={() => navigation.navigate("Dashboard")} style={styles.cancelBtn}>
                                <Text style={styles.cancelText}>Cancel</Text>
                            </Pressable>

                            <Button title="Save" onPress={save} style={{ flex: 1 }} />
                        </View>
                    )}
                </View>
            </ScrollView>
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    scroll: { padding: 16, paddingBottom: 140, gap: 14 },

    summaryCard: {
        backgroundColor: "rgba(255,255,255,0.88)",
        borderRadius: 16,
        padding: 14,
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.25)",
        shadowColor: "#000",
        shadowOpacity: 0.1,
        shadowRadius: 14,
        shadowOffset: { width: 0, height: 8 },
        elevation: 5,
    },
    summaryHead: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 10 },
    summaryIcon: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: "rgba(251,146,60,0.18)",
        alignItems: "center",
        justifyContent: "center",
    },
    summaryTitle: { fontSize: 14, fontWeight: "900", color: "#4A2311" },
    summaryGrid: { flexDirection: "row", gap: 12 },
    summaryBox: {
        flex: 1,
        backgroundColor: "rgba(255,247,237,0.75)",
        borderRadius: 14,
        padding: 12,
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.15)",
    },
    summaryLabel: { fontSize: 10, fontWeight: "900", color: "rgba(107,114,128,0.85)" },
    summaryValueOrange: { marginTop: 6, fontSize: 26, fontWeight: "900", color: "#EA580C" },
    summaryValueAmber: { marginTop: 6, fontSize: 26, fontWeight: "900", color: "#D97706" },

    card: {
        backgroundColor: "rgba(255,255,255,0.92)",
        borderRadius: 18,
        padding: 16,
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.2)",
        shadowColor: "#000",
        shadowOpacity: 0.12,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 10 },
        elevation: 6,
        gap: 12,
    },
    topRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },

    datePill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: "rgba(255,237,213,0.65)",
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.18)",
    },
    calIcon: { fontSize: 14 },
    dateText: { fontSize: 12, fontWeight: "900", color: "#9A3412" },

    historyBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 14,
        backgroundColor: "rgba(255,237,213,0.35)",
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.18)",
    },
    historyIcon: { fontSize: 14 },
    historyText: { fontSize: 12, fontWeight: "900", color: "#9A3412" },

    hint: { marginTop: -6, marginLeft: 2, fontSize: 11, fontWeight: "800", color: "rgba(107,114,128,0.85)" },

    marksRow: {
        backgroundColor: "rgba(255,237,213,0.55)",
        borderRadius: 14,
        paddingVertical: 14,
        paddingHorizontal: 14,
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.18)",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },
    marksLabel: { fontSize: 14, fontWeight: "900", color: "rgba(75,85,99,0.92)" },
    marksValue: { fontSize: 22, fontWeight: "900", color: "#EA580C" },

    btnRow: { flexDirection: "row", gap: 12, marginTop: 6 },

    // ✅ Visible Cancel button (not dependent on Button component)
    cancelBtn: {
        flex: 1,
        height: 48,
        borderRadius: 14,
        backgroundColor: "#fff",
        borderWidth: 2,
        borderColor: "rgba(156,163,175,0.75)",
        alignItems: "center",
        justifyContent: "center",
    },
    cancelText: {
        fontSize: 14,
        fontWeight: "900",
        color: "#374151",
    },
});
